/**
 * Password Generator Utility Functions
 * Generates secure random passwords with customizable options
 */

// Password configuration constants
export const PASSWORD_CONFIG = {
  MIN_LENGTH: 8,
  MAX_LENGTH: 128,
  DEFAULT_LENGTH: 16,
  MIN_COUNT: 1,
  MAX_COUNT: 10, // Aligned with UI slider max
};

// Entropy thresholds for strength ratings (bits)
export const ENTROPY_THRESHOLDS = {
  WEAK: 40,
  FAIR: 60,
  GOOD: 80,
  STRONG: 100,
};

// Character sets
const UPPERCASE = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const LOWERCASE = "abcdefghijklmnopqrstuvwxyz";
const NUMBERS = "0123456789";
const SYMBOLS = "!@#$%^&*()_+-=[]{}|;:,.<>?";

// Ambiguous characters that can be confused
const AMBIGUOUS = "0Ol1I";

// Pre-computed character sets for performance
let cachedCharSets = null;

/**
 * Gets or builds cached character sets
 * @returns {Object} Cached character sets
 */
function getCharacterSets() {
  if (!cachedCharSets) {
    cachedCharSets = {
      uppercase: UPPERCASE,
      lowercase: LOWERCASE,
      numbers: NUMBERS,
      symbols: SYMBOLS,
      uppercaseNoAmbiguous: UPPERCASE.split("")
        .filter((c) => !AMBIGUOUS.includes(c))
        .join(""),
      lowercaseNoAmbiguous: LOWERCASE.split("")
        .filter((c) => !AMBIGUOUS.includes(c))
        .join(""),
      numbersNoAmbiguous: NUMBERS.split("")
        .filter((c) => !AMBIGUOUS.includes(c))
        .join(""),
      symbolsNoAmbiguous: SYMBOLS, // Symbols don't have ambiguous chars
    };
  }
  return cachedCharSets;
}

/**
 * Validates that Web Crypto API is available
 * @throws {Error} If crypto API is not available
 */
function ensureCryptoAvailable() {
  if (!window.crypto || !window.crypto.getRandomValues) {
    throw new Error(
      "This browser cannot generate cryptographically secure random numbers."
    );
  }
}

/**
 * Validates password generation options
 * @param {Object} options - Password generation options
 * @throws {Error} If options are invalid
 */
function validateOptions(options) {
  const { length, useUppercase, useLowercase, useNumbers, useSymbols } =
    options;

  // Validate at least one character type is selected
  if (!useUppercase && !useLowercase && !useNumbers && !useSymbols) {
    throw new Error("Select at least one character type.");
  }

  // Validate length
  if (typeof length !== "number" || isNaN(length)) {
    throw new Error("The password length must be a number.");
  }

  if (
    length < PASSWORD_CONFIG.MIN_LENGTH ||
    length > PASSWORD_CONFIG.MAX_LENGTH
  ) {
    throw new Error(
      `The password length must be between ${PASSWORD_CONFIG.MIN_LENGTH} and ${PASSWORD_CONFIG.MAX_LENGTH} characters.`
    );
  }
}

/**
 * Builds character pool based on options
 * @param {Object} options - Password generation options
 * @returns {string} Character pool
 */
function buildCharacterPool(options) {
  const {
    useUppercase,
    useLowercase,
    useNumbers,
    useSymbols,
    excludeAmbiguous,
  } = options;
  const sets = getCharacterSets();

  let chars = "";

  if (excludeAmbiguous) {
    if (useUppercase) chars += sets.uppercaseNoAmbiguous;
    if (useLowercase) chars += sets.lowercaseNoAmbiguous;
    if (useNumbers) chars += sets.numbersNoAmbiguous;
    if (useSymbols) chars += sets.symbolsNoAmbiguous;
  } else {
    if (useUppercase) chars += sets.uppercase;
    if (useLowercase) chars += sets.lowercase;
    if (useNumbers) chars += sets.numbers;
    if (useSymbols) chars += sets.symbols;
  }

  return chars;
}

/**
 * Generates a cryptographically secure random password
 * @param {Object} options - Password generation options
 * @returns {string} Generated password
 * @throws {Error} If crypto API unavailable or invalid options
 */
export function generatePassword(options = {}) {
  const {
    length = PASSWORD_CONFIG.DEFAULT_LENGTH,
    useUppercase = true,
    useLowercase = true,
    useNumbers = true,
    useSymbols = true,
    excludeAmbiguous = false,
  } = options;

  // Ensure crypto API is available
  ensureCryptoAvailable();

  // Validate options
  validateOptions({
    length,
    useUppercase,
    useLowercase,
    useNumbers,
    useSymbols,
  });

  // Build character pool (uses cached filtered sets)
  const chars = buildCharacterPool({
    useUppercase,
    useLowercase,
    useNumbers,
    useSymbols,
    excludeAmbiguous,
  });

  // Generate password using crypto API
  const array = new Uint32Array(length);
  window.crypto.getRandomValues(array);

  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars[array[i] % chars.length];
  }

  // Ensure at least one character from each selected type
  password = ensureComplexity(password, options);

  return password;
}

/**
 * Ensures password contains at least one character from each selected type
 * @param {string} password - Generated password
 * @param {Object} options - Password generation options
 * @returns {string} Password with ensured complexity
 * @throws {Error} If crypto API unavailable
 */
function ensureComplexity(password, options) {
  const {
    useUppercase,
    useLowercase,
    useNumbers,
    useSymbols,
    excludeAmbiguous,
  } = options;

  // Ensure crypto API is available
  ensureCryptoAvailable();

  const sets = getCharacterSets();
  let modified = password;
  const array = new Uint32Array(4);
  window.crypto.getRandomValues(array);

  let position = 0;

  // Check and add uppercase if needed
  if (useUppercase && !/[A-Z]/.test(modified)) {
    const chars = excludeAmbiguous ? sets.uppercaseNoAmbiguous : sets.uppercase;
    const char = chars[array[0] % chars.length];
    modified = replaceAt(modified, position++, char);
  }

  // Check and add lowercase if needed
  if (useLowercase && !/[a-z]/.test(modified)) {
    const chars = excludeAmbiguous ? sets.lowercaseNoAmbiguous : sets.lowercase;
    const char = chars[array[1] % chars.length];
    modified = replaceAt(modified, position++, char);
  }

  // Check and add number if needed
  if (useNumbers && !/[0-9]/.test(modified)) {
    const chars = excludeAmbiguous ? sets.numbersNoAmbiguous : sets.numbers;
    const char = chars[array[2] % chars.length];
    modified = replaceAt(modified, position++, char);
  }

  // Check and add symbol if needed
  if (useSymbols && !/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(modified)) {
    const char = SYMBOLS[array[3] % SYMBOLS.length];
    modified = replaceAt(modified, position, char);
  }

  return modified;
}

/**
 * Replaces character at specific position
 * @param {string} str - String to modify
 * @param {number} index - Position to replace
 * @param {string} char - Character to insert
 * @returns {string} Modified string
 */
function replaceAt(str, index, char) {
  if (index >= str.length) index = str.length - 1;
  return str.substring(0, index) + char + str.substring(index + 1);
}

/**
 * Calculates password entropy in bits
 * @param {string} password - Password to analyze
 * @param {Object} options - Password generation options
 * @returns {number} Entropy in bits
 */
export function calculateEntropy(password, options = {}) {
  let poolSize = 0;

  if (options.useUppercase) poolSize += 26;
  if (options.useLowercase) poolSize += 26;
  if (options.useNumbers) poolSize += 10;
  if (options.useSymbols) poolSize += SYMBOLS.length;

  if (options.excludeAmbiguous) {
    // Reduce pool size by number of ambiguous characters in each set
    const ambiguousCount = AMBIGUOUS.length;
    poolSize -= ambiguousCount;
  }

  if (poolSize === 0) return 0;

  // Entropy = log2(poolSize^length) = length * log2(poolSize)
  return Math.floor(password.length * Math.log2(poolSize));
}

/**
 * Gets password strength rating based on entropy
 * @param {number} entropy - Entropy in bits
 * @returns {Object} Strength rating with label and color
 */
export function getStrengthRating(entropy) {
  if (entropy < ENTROPY_THRESHOLDS.WEAK) {
    return { label: "Weak", color: "red", percent: 20 };
  } else if (entropy < ENTROPY_THRESHOLDS.FAIR) {
    return { label: "Fair", color: "orange", percent: 40 };
  } else if (entropy < ENTROPY_THRESHOLDS.GOOD) {
    return { label: "Good", color: "yellow", percent: 60 };
  } else if (entropy < ENTROPY_THRESHOLDS.STRONG) {
    return { label: "Strong", color: "lime", percent: 80 };
  } else {
    return { label: "Very Strong", color: "green", percent: 100 };
  }
}

/**
 * Generates multiple passwords
 * @param {number} count - Number of passwords to generate
 * @param {Object} options - Password generation options
 * @returns {Array} Array of generated passwords
 * @throws {Error} If count is invalid
 */
export function generateMultiplePasswords(count, options) {
  if (typeof count !== "number" || isNaN(count)) {
    throw new Error("The number of passwords must be a number.");
  }

  if (count < PASSWORD_CONFIG.MIN_COUNT || count > PASSWORD_CONFIG.MAX_COUNT) {
    throw new Error(
      `The number of passwords must be between ${PASSWORD_CONFIG.MIN_COUNT} and ${PASSWORD_CONFIG.MAX_COUNT}.`
    );
  }

  const passwords = [];
  for (let i = 0; i < count; i++) {
    passwords.push(generatePassword(options));
  }
  return passwords;
}
